import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import UserProfilePopup from './UserProfilePopup';
import { useOrganization } from '../context/OrganizationContext';

import { API_URL } from '../constants/urls';

// Entity configuration with colors and icons
const ENTITY_CONFIGS = {
  okr: {
    icon: '🎯',
    color: '#3B82F6', // blue
    label: 'OKR',
    route: '/okrs',
  },
  segment: {
    icon: '📊',
    color: '#10B981', // green
    label: 'Segment',
    route: '/segments',
  },
  proposition: {
    icon: '💡',
    color: '#8B5CF6', // purple
    label: 'Proposition',
    route: '/propositions',
  },
  company: {
    icon: '🏢',
    color: '#F97316', // orange
    label: 'Company',
    route: '/companies',
  },
  user: {
    icon: '👤',
    color: '#6B7280', // gray
    label: 'User',
    route: null, // Users don't have a route, they show popup
  },
};

interface NarrativeDisplayProps {
  narrativeText: string;
  linkedOkrs?: Array<{
    okr_id: string;
    okr?: {
      id: string;
      objective: string;
    };
  }>;
}

export default function NarrativeDisplay({
  narrativeText,
  linkedOkrs = [],
}: NarrativeDisplayProps) {
  const navigate = useNavigate();
  const { selectedOrg } = useOrganization();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userCache, setUserCache] = useState<Map<string, any>>(new Map());

  // Fetch user data when popup is shown
  const fetchUserData = async (userId: string) => {
    // Check cache first
    if (userCache.has(userId)) {
      setSelectedUser(userCache.get(userId));
      return;
    }

    setLoadingUser(true);
    try {
      // Make sure we have an organization selected
      if (!selectedOrg || !selectedOrg.id) {
        console.error('No organization selected');
        setSelectedUser({ id: userId, email: 'No organization context' });
        setLoadingUser(false);
        return;
      }

      // Get organization members using the correct endpoint
      const membersResponse = await axios.get(
        `${API_URL}/organizations/${selectedOrg.id}/members`,
        { headers: getAuthHeaders() },
      );

      // Find the specific user in the members list
      const member = membersResponse.data.find(
        (m: any) => m.user_id === userId,
      );

      if (member) {
        const userData = {
          id: member.user_id,
          email: member.user_email,
          first_name: member.user_first_name,
          last_name: member.user_last_name,
          role: member.role,
          role_title: member.role_title,
          role_description: member.role_description,
        };
        setUserCache((prev) => new Map(prev).set(userId, userData));
        setSelectedUser(userData);
      } else {
        // User not found in current organization
        setSelectedUser({ id: userId, email: 'User not in organization' });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setSelectedUser({ id: userId, email: 'Unknown User' });
    } finally {
      setLoadingUser(false);
    }
  };

  const handleUserClick = async (userId: string, displayName: string) => {
    // Set initial data with display name
    setSelectedUser({ id: userId, email: displayName });
    setShowUserPopup(true);
    // Fetch full user data
    await fetchUserData(userId);
  };

  // Process the narrative text to temporarily replace tokens with placeholders
  // This allows ReactMarkdown to parse the markdown correctly
  const processedContent = useMemo(() => {
    if (!narrativeText) return { text: '', tokens: new Map() };

    // Token pattern: {{entity_type:uuid:display_name}}
    // This matches the 3-part format that MultiEntityMentionsEditor creates
    const tokenPattern =
      /\{\{(okr|segment|proposition|company|user):([a-f0-9-]+):([^}]+)\}\}/g;

    // Also handle react-mentions format for backward compatibility: @[display](entityType:id)
    const mentionPattern =
      /@\[([^\]]+)\]\((okr|segment|proposition|company|user):([a-f0-9-]+)\)/g;

    // Create a map to store token info
    const tokenMap = new Map();
    let tokenIndex = 0;

    // First, replace standard tokens with placeholders
    let processedText = narrativeText.replace(
      tokenPattern,
      (match, entityType, id, displayName) => {
        const placeholder = `⟦ENTITY_${tokenIndex}⟧`;
        tokenMap.set(placeholder, {
          entityType,
          id,
          displayName,
        });
        tokenIndex++;
        return placeholder;
      },
    );

    // Also replace react-mentions format (for backward compatibility)
    processedText = processedText.replace(
      mentionPattern,
      (match, displayName, entityType, id) => {
        const placeholder = `⟦ENTITY_${tokenIndex}⟧`;
        // Remove icon from display name if present
        const cleanDisplay = displayName.replace(/^[🎯📊💡🏢👤]\s*/u, '');
        tokenMap.set(placeholder, {
          entityType,
          id,
          displayName: cleanDisplay,
        });
        tokenIndex++;
        return placeholder;
      },
    );

    return { text: processedText, tokens: tokenMap };
  }, [narrativeText]);

  // Helper to transform strings that might contain our placeholders into
  // React elements with clickable entity chips
  const renderWithTokens = (text: string) => {
    const parts = text.split(/(⟦ENTITY_\d+⟧)/g);
    return parts.map((part, index) => {
      if (processedContent.tokens.has(part)) {
        const token = processedContent.tokens.get(part);
        const config =
          ENTITY_CONFIGS[token.entityType as keyof typeof ENTITY_CONFIGS];
        return (
          <button
            key={index}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (token.entityType === 'user') {
                handleUserClick(token.id, token.displayName);
              } else {
                switch (token.entityType) {
                  case 'okr':
                    navigate(`/okrs/${token.id}`);
                    break;
                  case 'segment':
                    navigate(`/segments/${token.id}`);
                    break;
                  case 'proposition':
                    navigate(`/propositions/${token.id}`);
                    break;
                  case 'company':
                    navigate(`/companies/${token.id}`);
                    break;
                  default:
                    if (config.route) {
                      navigate(config.route);
                    }
                }
              }
            }}
            className="inline-flex items-center px-2 py-1 mx-1 text-sm font-medium rounded-md transition-colors hover:opacity-80 cursor-pointer"
            style={{
              backgroundColor: `${config.color}20`,
              color: config.color,
              border: `1px solid ${config.color}40`,
            }}
            title={`${config.label}`}
          >
            <span className="mr-1">{config.icon}</span>
            {token.displayName}
          </button>
        );
      }
      return part;
    });
  };

  // Custom component renderer for ReactMarkdown
  const components = {
    // Override paragraph rendering to handle our entity tokens
    p: ({ children, ...props }: any) => {
      const processChildren = (children: any): any => {
        if (typeof children === 'string') {
          return renderWithTokens(children);
        }

        if (Array.isArray(children)) {
          return children.map((child, index) => {
            if (typeof child === 'string') {
              return renderWithTokens(child);
            }
            return child;
          });
        }

        return children;
      };

      return <p {...props}>{processChildren(children)}</p>;
    },
    // Override other text-containing elements similarly
    li: ({ children, ...props }: any) => {
      const processChildren = (children: any): any => {
        if (typeof children === 'string') {
          return renderWithTokens(children);
        }

        if (Array.isArray(children)) {
          return children.map((child, index) => {
            if (typeof child === 'string') {
              return renderWithTokens(child);
            }
            return child;
          });
        }

        return children;
      };

      return <li {...props}>{processChildren(children)}</li>;
    },
    // Headings & other inline text containers should also render tokens
    h1: ({ children, ...props }: any) => (
      <h1 {...props} className="text-3xl font-bold mb-4">
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </h1>
    ),
    h2: ({ children, ...props }: any) => (
      <h2 {...props} className="text-2xl font-bold mb-3">
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </h2>
    ),
    h3: ({ children, ...props }: any) => (
      <h3 {...props} className="text-xl font-bold mb-2">
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </h3>
    ),
    h4: ({ children, ...props }: any) => (
      <h4 {...props} className="text-lg font-semibold mb-2">
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </h4>
    ),
    blockquote: ({ children, ...props }: any) => (
      <blockquote
        {...props}
        className="border-l-4 border-gray-300 pl-4 italic my-4"
      >
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </blockquote>
    ),
    strong: ({ children, ...props }: any) => (
      <strong {...props} className="font-bold">
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </strong>
    ),
    em: ({ children, ...props }: any) => (
      <em {...props} className="italic">
        {Array.isArray(children)
          ? children.map((c, i) =>
              typeof c === 'string' ? (
                <React.Fragment key={i}>{renderWithTokens(c)}</React.Fragment>
              ) : (
                c
              ),
            )
          : typeof children === 'string'
            ? renderWithTokens(children)
            : children}
      </em>
    ),
    // Add more custom renderers for headings if needed
    // Styling for other markdown elements
    ul: ({ children, ...props }: any) => (
      <ul {...props} className="list-disc list-inside mb-4 space-y-1">
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: any) => (
      <ol {...props} className="list-decimal list-inside mb-4 space-y-1">
        {children}
      </ol>
    ),
    code: ({ inline, children, ...props }: any) => {
      return inline ? (
        <code
          {...props}
          className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono"
        >
          {children}
        </code>
      ) : (
        <code
          {...props}
          className="block bg-gray-100 p-3 rounded my-2 text-sm font-mono overflow-x-auto"
        >
          {children}
        </code>
      );
    },
    hr: () => <hr className="my-6 border-gray-300" />,
    a: ({ children, href, ...props }: any) => (
      <a
        {...props}
        href={href}
        className="text-blue-600 hover:text-blue-800 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    // keep defaults for strong/em
  };

  // If no content, show placeholder
  if (!narrativeText || narrativeText.trim() === '') {
    return (
      <div className="text-gray-500 italic">
        No narrative content available.
      </div>
    );
  }

  return (
    <>
      <div className="prose prose-lg max-w-none [&_p]:mb-6">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {processedContent.text}
        </ReactMarkdown>
      </div>

      {/* User Profile Popup */}
      <UserProfilePopup
        isOpen={showUserPopup}
        onClose={() => {
          setShowUserPopup(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        loading={loadingUser}
      />
    </>
  );
}
